"use client";

import { PortableText, PortableTextComponents } from "@portabletext/react";
import type { PortableTextBlock } from "@portabletext/types";
import Image from "next/image";
import Link from "next/link";
import React, { useCallback, useState } from "react";

import { useGetFreeAssessmentModal } from "@/components/campaign/GetFreeAssessmentModalContext";

/** Extract anchor id from a link like /path#get-free-assessment or #get-free-assessment */
function getHashIdFromLink(link: string): string | null {
  const trimmed = (link ?? "").trim();
  const hashIndex = trimmed.indexOf("#");
  if (hashIndex < 0 || hashIndex >= trimmed.length - 1) return null;
  const hashId = trimmed.slice(hashIndex + 1).split("?")[0].trim();
  return hashId || null;
}

/** Scroll to element by id; if not in DOM yet, poll then scroll. */
function scrollToHashId(hashId: string): void {
  const el = document.getElementById(hashId);
  if (el) {
    el.scrollIntoView({ behavior: "smooth", block: "start" });
    window.history.replaceState(null, "", `#${hashId}`);
    return;
  }
  window.history.replaceState(null, "", `#${hashId}`);
  const deadline = Date.now() + 8000;
  const interval = setInterval(() => {
    const target = document.getElementById(hashId);
    if (target) {
      clearInterval(interval);
      target.scrollIntoView({ behavior: "smooth", block: "start" });
    } else if (Date.now() > deadline) clearInterval(interval);
  }, 150);
}

export interface WServicesTabContent {
  name: string;
  iconUrl?: string;
  iconAlt?: string;
  /** Plain string (legacy) or Portable Text blocks for full HTML/rich content */
  bodyText?: string | PortableTextBlock[];
  ctaLabel?: string;
  ctaLink?: string;
  imageUrl?: string;
  imageAlt?: string;
}

const portableTextComponents: PortableTextComponents = {
  types: {
    table: ({ value }: { value: any }) => {
      const { rows, caption } = value ?? {};
      if (!rows || rows.length === 0) return null;

      return (
        <div className="my-6 w-full overflow-x-auto">
          {caption && (
            <p className="mb-2 text-base font-semibold text-gray-900">
              {caption}
            </p>
          )}
          <table className="min-w-full border-collapse border border-gray-200">
            <tbody>
              {rows.map((row: any, rowIndex: number) => (
                <tr key={rowIndex}>
                  {row.cells?.map((cell: any, cellIndex: number) => {
                    const CellTag = cell.isHeader ? "th" : "td";
                    return (
                      <CellTag
                        key={cellIndex}
                        className={`border border-gray-200 px-3 py-2 text-left text-gray-700 ${
                          cell.isHeader
                            ? "bg-gray-100 font-semibold text-gray-900"
                            : ""
                        }`}
                        colSpan={cell.colspan || 1}
                        rowSpan={cell.rowspan || 1}
                      >
                        {cell.content && cell.content.length > 0 ? (
                          <PortableText
                            value={cell.content}
                            components={portableTextComponents}
                          />
                        ) : (
                          "\u00A0"
                        )}
                      </CellTag>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    },
  },
  block: {
    h2: ({ children }) => (
      <h2 className="mb-3 mt-6 text-xl font-bold text-gray-900">
        {children}
      </h2>
    ),
    h3: ({ children }) => (
      <h3 className="mb-2 mt-4 text-lg font-semibold text-gray-900">
        {children}
      </h3>
    ),
    h4: ({ children }) => (
      <h4 className="mb-2 mt-3 text-base font-semibold text-gray-900">
        {children}
      </h4>
    ),
    blockquote: ({ children }) => (
      <blockquote className="border-l-4 border-violet-200 pl-4 italic text-gray-700">
        {children}
      </blockquote>
    ),
    normal: ({ children }) =>
      children ? <p className="mb-3 last:mb-0">{children}</p> : null,
  },
  list: {
    bullet: ({ children }) => (
      <ol className="w-services-list-circle text-gray-700">{children}</ol>
    ),
    number: ({ children }) => (
      <ol className="w-services-list-circle text-gray-700">{children}</ol>
    ),
  },
  listItem: {
    bullet: ({ children }) => <li className="text-gray-700">{children}</li>,
    number: ({ children }) => <li className="text-gray-700">{children}</li>,
  },
  marks: {
    strong: ({ children }) => (
      <strong className="font-semibold text-gray-900">{children}</strong>
    ),
    em: ({ children }) => <em className="italic">{children}</em>,
    underline: ({ children }) => <u>{children}</u>,
    code: ({ children }) => (
      <code className="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-sm">
        {children}
      </code>
    ),
    link: ({ children, value }) => {
      const href = value?.href ?? "#";
      const isInternal =
        typeof href === "string" &&
        (href.startsWith("/") || href.startsWith("#"));
      if (isInternal) {
        return (
          <Link
            href={href}
            className="text-violet-700 underline hover:text-violet-800"
          >
            {children}
          </Link>
        );
      }
      return (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="text-violet-700 underline hover:text-violet-800"
        >
          {children}
        </a>
      );
    },
    internalLink: ({ children, value }) => (
      <Link
        href={value?.href ?? "#"}
        className="text-violet-700 underline hover:text-violet-800"
      >
        {children}
      </Link>
    ),
  },
};

const MAX_WIDTH_CLASSES: Record<string, string> = {
  default: "max-w-4xl",
  wide: "max-w-6xl",
  full: "max-w-none",
};

function CTALink({
  hashId,
  label,
  buttonClass,
}: {
  hashId: string;
  label: string;
  buttonClass: string;
}) {
  const modalContext = useGetFreeAssessmentModal();
  if (hashId === "get-free-assessment" && modalContext?.openModal) {
    return (
      <button
        type="button"
        className={buttonClass}
        aria-label={label}
        onClick={() => modalContext.openModal()}
      >
        {label}
      </button>
    );
  }
  return (
    <a
      href={`#${hashId}`}
      className={buttonClass}
      aria-label={label}
      onClick={(e) => {
        e.preventDefault();
        scrollToHashId(hashId);
      }}
    >
      {label}
    </a>
  );
}

export interface WServicesSectionProps {
  title?: string;
  tabs?: WServicesTabContent[];
  activeTabColor?: string;
  inactiveTabBackgroundColor?: string;
  maxWidth?: "default" | "wide" | "full";
}

const DEFAULT_ACTIVE_COLOR = "#4c1d95";
const DEFAULT_INACTIVE_BG = "#f3f4f6";
const TITLE_LINE_COLOR = "#2e1065";

export default function WServicesSection({
  title,
  tabs = [],
  activeTabColor = DEFAULT_ACTIVE_COLOR,
  inactiveTabBackgroundColor = DEFAULT_INACTIVE_BG,
  maxWidth = "wide",
}: WServicesSectionProps) {
  const [activeIndex, setActiveIndex] = useState(0);
  const activeTab = tabs[activeIndex];

  const handleTabClick = useCallback((index: number) => {
    setActiveIndex(index);
  }, []);

  const handleTabKeyDown = useCallback(
    (e: React.KeyboardEvent, index: number) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        setActiveIndex(index);
      }
      if (e.key === "ArrowRight" && index < tabs.length - 1) {
        e.preventDefault();
        setActiveIndex(index + 1);
      }
      if (e.key === "ArrowLeft" && index > 0) {
        e.preventDefault();
        setActiveIndex(index - 1);
      }
    },
    [tabs.length]
  );

  const validTabs = tabs.filter((t) => t?.name);
  if (validTabs.length === 0) return null;

  const resolvedActiveColor =
    activeTabColor === "custom" ? DEFAULT_ACTIVE_COLOR : activeTabColor;
  const resolvedInactiveBg =
    inactiveTabBackgroundColor === "transparent"
      ? "transparent"
      : inactiveTabBackgroundColor || DEFAULT_INACTIVE_BG;

  const maxWidthClass = MAX_WIDTH_CLASSES[maxWidth] ?? MAX_WIDTH_CLASSES.wide;

  return (
    <section className="py-8 md:py-12" aria-label={title || "Services"}>
      <div className={`mx-auto ${maxWidthClass} px-4 md:px-6`}>
        {title && title.trim() && (
          <div className="mb-6 grid w-full grid-cols-[1fr_auto_1fr] items-center gap-3">
            <div
              className="h-0.5 w-[35%] min-w-[80px] justify-self-end"
              style={{
                background: `linear-gradient(to right, transparent, ${TITLE_LINE_COLOR})`,
              }}
              aria-hidden
            />
            <h2 className="text-center font-extrabold uppercase tracking-wide text-violet-950 md:text-xl lg:text-2xl">
              {title.trim()}
            </h2>
            <div
              className="h-0.5 w-[35%] min-w-[80px] justify-self-start"
              style={{
                background: `linear-gradient(to right, ${TITLE_LINE_COLOR}, transparent)`,
              }}
              aria-hidden
            />
          </div>
        )}
        {/* Tabs row — fixed-height cards so all stay even; arrow extends below active only */}
        <div
          className="mb-8 grid w-full grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4 md:grid-cols-6"
          role="tablist"
          aria-label="Service tabs"
        >
          {validTabs.map((tab, index) => {
            const isActive = index === activeIndex;
            const bgColor = isActive ? resolvedActiveColor : resolvedInactiveBg;

            return (
              <div
                key={index}
                className="relative flex h-[120px] flex-col sm:h-[130px] md:h-[140px]"
              >
                <button
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  aria-controls={`services-panel-${index}`}
                  id={`services-tab-${index}`}
                  tabIndex={isActive ? 0 : -1}
                  className="flex h-full min-w-0 flex-col items-center justify-center rounded-xl p-4 transition-colors focus:outline-none focus:ring-0"
                  style={{ backgroundColor: bgColor }}
                  onClick={() => handleTabClick(index)}
                  onKeyDown={(e) => handleTabKeyDown(e, index)}
                >
                  {tab.iconUrl && (
                    <span
                      className="mb-2 flex size-12 shrink-0 items-center justify-center overflow-hidden rounded-full md:size-14"
                      style={{
                        backgroundColor: isActive ? "#ffffff" : "rgba(255,255,255,0.9)",
                      }}
                      aria-hidden
                    >
                      <Image
                        src={tab.iconUrl}
                        alt=""
                        width={56}
                        height={56}
                        className="size-7 object-contain md:size-8"
                        unoptimized={
                          tab.iconUrl.startsWith("http") &&
                          !tab.iconUrl.includes("trichos")
                        }
                      />
                    </span>
                  )}
                  <span
                    className="text-center text-sm font-medium md:text-base"
                    style={{
                      color: isActive ? "#ffffff" : "#374151",
                    }}
                  >
                    {tab.name}
                  </span>
                </button>
                {isActive && (
                  <span
                    className="absolute left-1/2 top-full size-0 -translate-x-1/2 border-l-[10px] border-r-[10px] border-t-[12px] border-l-transparent border-r-transparent"
                    style={{ borderTopColor: resolvedActiveColor }}
                    aria-hidden
                  />
                )}
              </div>
            );
          })}
        </div>

        {/* Content panels — two columns on desktop; mobile: image first, then text */}
        {activeTab && (
          <div
            id={`services-panel-${activeIndex}`}
            role="tabpanel"
            aria-labelledby={`services-tab-${activeIndex}`}
            className="grid gap-8 md:grid-cols-[1fr,minmax(280px,40%)] md:items-stretch md:gap-10"
          >
            {/* Text column: second on mobile (order-2), first on desktop (md:order-none) */}
            <div className="order-2 flex flex-col justify-center md:order-none">
              {activeTab.bodyText != null &&
                (Array.isArray(activeTab.bodyText) ? (
                  <div className="prose prose-gray max-w-none text-gray-700 md:text-lg prose-p:mb-3 prose-headings:text-gray-900 prose-a:text-violet-700 prose-a:underline hover:prose-a:text-violet-800">
                    <PortableText
                      value={activeTab.bodyText}
                      components={portableTextComponents}
                    />
                  </div>
                ) : (
                  <div className="prose prose-gray max-w-none text-gray-700 md:text-lg">
                    <p className="whitespace-pre-line">{activeTab.bodyText}</p>
                  </div>
                ))}
              {activeTab.ctaLabel && activeTab.ctaLink && (() => {
                const hashId = getHashIdFromLink(activeTab.ctaLink);
                const buttonClass =
                  "inline-flex items-center justify-center rounded-full bg-black px-6 py-3 text-sm font-semibold text-[#F7E9AE] shadow transition hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-[#F7E9AE] focus:ring-offset-2";
                if (hashId) {
                  return (
                    <div className="mt-6">
                      <CTALink
                        hashId={hashId}
                        label={activeTab.ctaLabel}
                        buttonClass={buttonClass}
                      />
                    </div>
                  );
                }
                return (
                  <div className="mt-6">
                    <Link
                      href={activeTab.ctaLink}
                      className={buttonClass}
                      aria-label={activeTab.ctaLabel}
                    >
                      {activeTab.ctaLabel}
                    </Link>
                  </div>
                );
              })()}
            </div>

            {/* Image column: first on mobile (order-1), second on desktop (md:order-none); fit to width, no crop */}
            {activeTab.imageUrl && (
              <div className="order-1 w-full md:order-none">
                <Image
                  src={activeTab.imageUrl}
                  alt={activeTab.imageAlt || activeTab.name || "Service"}
                  width={800}
                  height={600}
                  className="w-full rounded-xl object-contain object-left-top"
                  sizes="(min-width: 768px) 40vw, 100vw"
                  unoptimized={
                    activeTab.imageUrl.startsWith("http") &&
                    !activeTab.imageUrl.includes("trichos")
                  }
                />
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
